import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { copyrightYears } from '../lib/utils';
import { 
  MessageSquare, 
  Send, 
  Users, 
  Megaphone, 
  Shield,
  ChevronRight,
  ChevronDown,
  Zap,
  BarChart3,
  Clock,
  Mail,
  ArrowRight,
  Menu,
  X,
  Loader2,
  Check,
  Star,
  Info
} from 'lucide-react';

// Estilos fixos para forçar tema claro
const lightThemeStyle = {
  '--background': '210 33% 98%',
  '--foreground': '220 15% 10%',
  '--card': '0 0% 100%',
  '--card-foreground': '220 15% 10%',
  '--primary': '181 77% 24%',
  '--primary-foreground': '160 76% 97%',
  '--muted-foreground': '220 9% 46%',
};

// Ícones para features
const featureIcons = [Send, Megaphone, Users, BarChart3, Shield, Clock];

// Conteúdo padrão (fallback)
const defaultContent = {
  hero: {
    badge: "Seu WhatsApp Turbinado",
    title_line1: "Envie mensagens em massa",
    title_line2: "de forma simples e eficiente",
    subtitle: "Gerencie contatos, crie campanhas e acompanhe resultados em tempo real. Tudo em uma plataforma intuitiva e poderosa."
  },
  stats: [
    { value: "10k+", label: "Mensagens Enviadas", description: "Capacidade de envio em massa" },
    { value: "99%", label: "Taxa de Entrega", description: "Confiabilidade nas entregas" },
    { value: "24/7", label: "Disponibilidade", description: "Sistema sempre disponível" }
  ],
  features: [
    { title: "Envio em Massa", description: "Envie mensagens para centenas de contatos de uma só vez, de forma rápida e eficiente." },
    { title: "Campanhas", description: "Crie e gerencie campanhas de marketing com acompanhamento em tempo real." },
    { title: "Gestão de Grupos", description: "Organize seus contatos em grupos para segmentar suas comunicações." },
    { title: "Relatórios", description: "Acompanhe métricas de envio, entrega e falhas em dashboards intuitivos." },
    { title: "Multi-usuários", description: "Diferentes perfis de acesso: Admin, Editor, Envio e Somente Leitura." },
    { title: "Histórico Completo", description: "Acesse o histórico de todas as mensagens enviadas com filtros avançados." }
  ],
  pricing: {
    section_title: "Planos",
    section_subtitle: "Escolha o plano ideal para o seu negócio",
    plans: [
      {
        name: "Trial",
        description: "Experimente grátis por alguns dias",
        price_monthly: "0",
        highlighted: false,
        cta_url: "/login",
        cta_text: "Começar Grátis",
        features: [
          { text: "1 número WhatsApp", tooltip: "Conecte seu número de WhatsApp ou WhatsApp Business" },
          { text: "100 mensagens", tooltip: "Limite total para teste" },
          { text: "Acesso a todas as funcionalidades", tooltip: "Sem restrição de recursos durante o trial" },
          { text: "Sem cartão de crédito", tooltip: "Você não precisa cadastrar cartão" }
        ]
      },
      {
        name: "Light",
        description: "Ideal para pequenos negócios",
        price_monthly: "97",
        highlighted: false,
        cta_url: "/login",
        cta_text: "Começar Agora",
        features: [
          { text: "1 número WhatsApp", tooltip: "Conecte seu número de WhatsApp ou WhatsApp Business" },
          { text: "10.000 mensagens de texto", tooltip: "Mensagens normais de texto + emojis" },
          { text: "2.500 mensagens de mídia", tooltip: "Mensagens com texto + emojis + imagens" },
          { text: "500 mensagens na fila", tooltip: "Quantidade máxima de mensagens aguardando envio simultaneamente na sua campanha" },
          { text: "6 mensagens por minuto", tooltip: "" }
        ]
      },
      {
        name: "Professional",
        description: "Para empresas em crescimento",
        price_monthly: "197",
        highlighted: true,
        cta_url: "/login",
        cta_text: "Começar Agora",
        features: [
          { text: "1 número WhatsApp", tooltip: "Conecte seu número de WhatsApp ou WhatsApp Business" },
          { text: "30.000 mensagens de texto", tooltip: "Mensagens normais de texto + emojis" },
          { text: "10.000 mensagens de mídia", tooltip: "Mensagens com texto + emojis + imagens" },
          { text: "2.000 mensagens na fila", tooltip: "Quantidade máxima de mensagens aguardando envio simultaneamente na sua campanha" },
          { text: "15 mensagens por minuto", tooltip: "" }
        ]
      },
      {
        name: "Business",
        description: "Para operações de grande escala",
        price_monthly: "397",
        highlighted: false,
        cta_url: "/login",
        cta_text: "Começar Agora",
        features: [
          { text: "1 número WhatsApp", tooltip: "Conecte seu número de WhatsApp ou WhatsApp Business" },
          { text: "Mensagens de texto ilimitadas *", tooltip: "Mensagens normais de texto + emojis" },
          { text: "Mensagens de mídia ilimitadas *", tooltip: "Mensagens com texto + emojis + imagens" },
          { text: "10.000 mensagens na fila", tooltip: "Quantidade máxima de mensagens aguardando envio simultaneamente na sua campanha" },
          { text: "100 mensagens por minuto", tooltip: "" }
        ]
      }
    ],
    disclaimer: "* Um limite antiabuso de 200.000 mensagens por mês se aplica pelo próprio WhatsApp."
  },
  steps: [
    { title: "Conecte seu WhatsApp", description: "Escaneie o QR Code para conectar sua conta do WhatsApp ao sistema. Processo simples e seguro." },
    { title: "Importe seus Contatos", description: "Adicione contatos manualmente ou importe uma planilha do Excel. Organize em grupos para melhor segmentação." },
    { title: "Crie suas Campanhas", description: "Monte suas campanhas com mensagens personalizadas e selecione os grupos de destino." },
    { title: "Envie e Acompanhe", description: "Dispare suas mensagens e acompanhe em tempo real o progresso e resultados." }
  ],
  faqs: [
    { question: "Quantas mensagens posso enviar por dia?", answer: "O limite de envio depende das políticas do WhatsApp. Recomendamos envios graduais para manter a saúde da sua conta." },
    { question: "Posso usar meu número pessoal?", answer: "Sim, você pode conectar qualquer número de WhatsApp. Porém, recomendamos usar um número dedicado para comunicações empresariais." },
    { question: "Como funciona a importação de contatos?", answer: "Você pode importar contatos por uma planilha do Excel com nome e telefone, e o sistema oferece um modelo pronto para baixar. Também é possível adicionar contatos manualmente." },
    { question: "O sistema é seguro?", answer: "Sim, utilizamos criptografia e seguimos as melhores práticas de segurança. Seus dados são protegidos." },
    { question: "Posso ter múltiplos usuários?", answer: "Sim, o sistema suporta múltiplos usuários com diferentes níveis de permissão." }
  ],
  contact: {
    title: "Envie uma mensagem",
    subtitle: "Tem alguma dúvida ou precisa de mais informações? Entre em contato conosco.",
    whatsapp_number: "5511999999999",
    whatsapp_text: "Falar pelo WhatsApp",
    email_label: "contato@watizap.com.br"
  },
  cta: {
    title: "Pronto para turbinar seu WhatsApp?",
    subtitle: "Comece a enviar mensagens em massa de forma profissional hoje mesmo."
  },
  header: {
    manage_plan_url: "/login",
    manage_plan_text: "Gerenciar meu plano"
  }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [contactForm, setContactForm] = useState({ email: '', message: '' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sendingContact, setSendingContact] = useState(false);
  const [content, setContent] = useState(defaultContent);
  const [loadingContent, setLoadingContent] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const handlePlanCTA = async (plan) => {
    const customUrl = plan.cta_url;
    // Custom URL configured by Super Admin → respect it
    if (customUrl && customUrl.startsWith('http')) {
      window.open(customUrl, '_blank');
      return;
    }
    if (customUrl && customUrl !== '/signup' && customUrl !== '/login') {
      navigate(customUrl);
      return;
    }
    // Paid plan with stripe_price_id → start email verification flow
    if (plan.stripe_price_id) {
      navigate(`/signup/email?plan=${encodeURIComponent(plan.name)}`);
      return;
    }
    // No Stripe Price ID → fallback to traditional signup form (Trial / free plans)
    navigate(`/signup?plan=${encodeURIComponent(plan.name)}`);
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms`);
      if (response.ok) {
        const data = await response.json();
        // Merge with defaults to ensure all fields exist
        setContent({
          ...defaultContent,
          ...data,
          hero: { ...defaultContent.hero, ...data.hero },
          contact: { ...defaultContent.contact, ...data.contact },
          cta: { ...defaultContent.cta, ...data.cta },
          header: { ...defaultContent.header, ...data.header },
          pricing: { 
            ...defaultContent.pricing, 
            ...data.pricing,
            plans: data.pricing?.plans?.length ? data.pricing.plans : defaultContent.pricing.plans
          },
          stats: data.stats?.length ? data.stats : defaultContent.stats,
          features: data.features?.length ? data.features : defaultContent.features,
          steps: data.steps?.length ? data.steps : defaultContent.steps,
          faqs: data.faqs?.length ? data.faqs : defaultContent.faqs,
        });
      }
    } catch (error) {
      console.log('Using default content');
    } finally {
      setLoadingContent(false);
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.email || !contactForm.message) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }
    
    if (!validateEmail(contactForm.email)) {
      toast.error('E-mail inválido. Use o formato: nome@dominio.com');
      return;
    }
    
    setSendingContact(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erro ao enviar mensagem');
      }
      
      toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');
      setContactForm({ email: '', message: '' });
    } catch (error) {
      let errorMessage = 'Erro ao enviar mensagem. Tente novamente.';
      if (error.message && !error.message.includes('body stream')) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setSendingContact(false);
    }
  };

  if (loadingContent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={lightThemeStyle}>
      <Seo
        title="Disparo de Mensagens em Massa no WhatsApp | Watizap"
        description="Envie mensagens, campanhas e atendimentos pelo WhatsApp de forma profissional. Gestão de contatos, agendamento, multi-usuários e relatórios. Teste grátis."
        path="/"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 cursor-pointer"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-600 to-teal-600 flex items-center justify-center shadow-lg shadow-teal-500/20">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">Watizap</span>
          </button>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => document.getElementById('funcionalidades').scrollIntoView({ behavior: 'smooth' })}
              className="text-gray-600 hover:text-teal-700 transition-colors text-base font-medium"
            >
              Funcionalidades
            </button>
            <button 
              onClick={() => document.getElementById('planos').scrollIntoView({ behavior: 'smooth' })}
              className="text-gray-600 hover:text-teal-700 transition-colors text-base font-medium"
            >
              Planos
            </button>
            <button 
              onClick={() => document.getElementById('como-funciona').scrollIntoView({ behavior: 'smooth' })}
              className="text-gray-600 hover:text-teal-700 transition-colors text-base font-medium"
            >
              Como Funciona
            </button>
            <button 
              onClick={() => document.getElementById('faq').scrollIntoView({ behavior: 'smooth' })}
              className="text-gray-600 hover:text-teal-700 transition-colors text-base font-medium"
            >
              FAQ
            </button>
            <button 
              onClick={() => document.getElementById('contato').scrollIntoView({ behavior: 'smooth' })}
              className="text-gray-600 hover:text-teal-700 transition-colors text-base font-medium"
            >
              Contato
            </button>
          </nav>
          
          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={() => {
                const url = content.header?.manage_plan_url || '/login';
                if (url.startsWith('http')) {
                  window.open(url, '_blank');
                } else {
                  navigate(url);
                }
              }}
              className="btn-glow border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white transition-colors"
              data-testid="header-manage-plan-btn"
            >
              {content.header?.manage_plan_text || 'Gerenciar meu plano'}
            </Button>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-teal-600 to-teal-600 hover:from-teal-700 hover:to-teal-700 text-white shadow-lg shadow-teal-500/20"
            >
              Entrar
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-teal-700 transition-colors"
            aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileMenuOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-4">
            <button 
              onClick={() => { setMobileMenuOpen(false); document.getElementById('funcionalidades').scrollIntoView({ behavior: 'smooth' }); }} 
              className="block w-full text-left text-gray-600 hover:text-teal-700 transition-colors font-medium py-2"
            >
              Funcionalidades
            </button>
            <button 
              onClick={() => { setMobileMenuOpen(false); document.getElementById('planos').scrollIntoView({ behavior: 'smooth' }); }} 
              className="block w-full text-left text-gray-600 hover:text-teal-700 transition-colors font-medium py-2"
            >
              Planos
            </button>
            <button 
              onClick={() => { setMobileMenuOpen(false); document.getElementById('como-funciona').scrollIntoView({ behavior: 'smooth' }); }} 
              className="block w-full text-left text-gray-600 hover:text-teal-700 transition-colors font-medium py-2"
            >
              Como Funciona
            </button>
            <button 
              onClick={() => { setMobileMenuOpen(false); document.getElementById('faq').scrollIntoView({ behavior: 'smooth' }); }} 
              className="block w-full text-left text-gray-600 hover:text-teal-700 transition-colors font-medium py-2"
            >
              FAQ
            </button>
            <button 
              onClick={() => { setMobileMenuOpen(false); document.getElementById('contato').scrollIntoView({ behavior: 'smooth' }); }} 
              className="block w-full text-left text-gray-600 hover:text-teal-700 transition-colors font-medium py-2"
            >
              Contato
            </button>
            <div className="flex flex-col gap-2 pt-2">
              <Button 
                variant="outline"
                onClick={() => { 
                  setMobileMenuOpen(false);
                  const url = content.header?.manage_plan_url || '/login';
                  if (url.startsWith('http')) {
                    window.open(url, '_blank');
                  } else {
                    navigate(url);
                  }
                }}
                className="btn-glow w-full border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white transition-colors"
              >
                {content.header?.manage_plan_text || 'Gerenciar meu plano'}
              </Button>
              <Button 
                onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                className="w-full bg-gradient-to-r from-teal-600 to-teal-600 hover:from-teal-700 hover:to-teal-700 text-white shadow-lg shadow-teal-500/20"
              >
                Entrar
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-teal-500/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-teal-500/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-teal-500/30">
            <Zap className="w-4 h-4" />
            {content.hero.badge}
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {content.hero.title_line1}<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-teal-400">{content.hero.title_line2}</span>
          </h1>
          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
            {content.hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => document.getElementById('planos').scrollIntoView({ behavior: 'smooth' })}
              className="btn-shine-wrapper bg-gradient-to-r from-teal-500 to-teal-500 hover:from-teal-600 hover:to-teal-600 text-white text-lg px-7 py-4 h-auto shadow-xl shadow-teal-500/30"
              data-testid="hero-cta-btn"
            >
              Ver Planos
            </Button>
          </div>
        </div>

        {/* Hero Image — Real Dashboard Screenshot */}
        <div className="max-w-5xl mx-auto mt-16 relative z-10 px-4 md:px-0">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Browser Window Header */}
            <div className="bg-gray-100 px-3 md:px-4 py-2 md:py-3 flex items-center gap-2 border-b">
              <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-red-400"></div>
              <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-yellow-400"></div>
              <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-teal-400"></div>
              <span className="text-[10px] md:text-xs text-gray-500 ml-2 md:ml-4 truncate">Watizap - Dashboard</span>
            </div>
            {/* Real screenshot */}
            <img
              src="/dashboard-preview.png"
              alt="Painel do Watizap com gráficos de mensagens, contatos e campanhas em tempo real"
              loading="eager"
              decoding="async"
              className="w-full h-auto block"
              data-testid="hero-dashboard-image"
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-6 bg-gradient-to-r from-teal-600 to-teal-600 -mt-10 relative z-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {content.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-teal-100 font-medium mb-1">{stat.label}</div>
                <div className="text-teal-200/70 text-sm">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="funcionalidades" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">Funcionalidades</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">Tudo que você precisa</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Funcionalidades pensadas para otimizar sua comunicação via WhatsApp</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.features.map((feature, index) => {
              const Icon = featureIcons[index] || Send;
              return (
                <div key={index} className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 group hover:-translate-y-1">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500 to-teal-500 flex items-center justify-center mb-6 shadow-lg shadow-teal-500/20 group-hover:scale-110 transition-transform">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="planos" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">{content.pricing.section_title}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">{content.pricing.section_subtitle}</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-5">
            {content.pricing.plans.map((plan, index) => (
              <div 
                key={index} 
                className={`relative rounded-2xl p-6 lg:p-7 transition-all duration-300 hover:-translate-y-2 hover:z-30 ${
                  plan.highlighted 
                    ? 'bg-gradient-to-br from-teal-600 to-teal-600 text-white shadow-2xl shadow-teal-500/30 scale-105 z-20' 
                    : 'bg-white border border-gray-200 hover:shadow-xl z-10'
                }`}
                data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-amber-400 text-amber-900 text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1 shadow-lg whitespace-nowrap">
                      <Star className="w-3 h-3 fill-current" /> MAIS POPULAR
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className={`text-2xl font-bold mb-2 ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm ${plan.highlighted ? 'text-teal-100' : 'text-gray-500'}`}>
                    {plan.description}
                  </p>
                </div>

                <div className="text-center mb-8">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className={`text-lg ${plan.highlighted ? 'text-teal-100' : 'text-gray-500'}`}>R$</span>
                    <span className={`text-5xl font-bold ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                      {plan.price_monthly}
                    </span>
                    <span className={`text-lg ${plan.highlighted ? 'text-teal-100' : 'text-gray-500'}`}>/mês</span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, fIndex) => {
                    // Suporte para formato antigo (string) e novo (objeto com text e tooltip)
                    const featureText = typeof feature === 'string' ? feature : feature.text;
                    const tooltipText = typeof feature === 'string' ? null : (feature.tooltip || null);
                    
                    return (
                      <li key={fIndex} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'bg-white/20' : 'bg-teal-100'
                        }`}>
                          <Check className={`w-3 h-3 ${plan.highlighted ? 'text-white' : 'text-teal-600'}`} />
                        </div>
                        <span className={`text-sm flex items-center gap-1.5 ${plan.highlighted ? 'text-white' : 'text-gray-600'}`}>
                          {featureText}
                          {tooltipText && (
                            <span className="relative group">
                              <Info className={`w-3.5 h-3.5 cursor-help ${plan.highlighted ? 'text-white/70 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`} />
                              <span className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg w-48 text-center ${
                                plan.highlighted 
                                  ? 'bg-white text-gray-800' 
                                  : 'bg-gray-800 text-white'
                              }`}>
                                {tooltipText}
                                <span className={`absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent ${
                                  plan.highlighted 
                                    ? 'border-t-white' 
                                    : 'border-t-gray-800'
                                }`}></span>
                              </span>
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                <Button 
                  onClick={() => handlePlanCTA(plan)}
                  disabled={checkoutLoading === plan.name}
                  className={`w-full py-6 h-auto text-base font-semibold ${
                    plan.highlighted 
                      ? 'bg-white text-teal-600 hover:bg-gray-100' 
                      : 'bg-gradient-to-r from-teal-500 to-teal-500 hover:from-teal-600 hover:to-teal-600 text-white'
                  }`}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  {checkoutLoading === plan.name ? 'Carregando...' : (plan.cta_text || 'Começar Agora')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ))}
          </div>

          {content.pricing.disclaimer && (
            <p className="text-center text-sm text-gray-500 mt-8">
              {content.pricing.disclaimer}
            </p>
          )}
        </div>
      </section>

      {/* How it Works Section */}
      <section id="como-funciona" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">Metodologia</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">Como Funciona</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Comece a usar em poucos minutos com nossa metodologia simples</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {content.steps.map((step, index) => (
              <div key={index} className="relative">
                {index < content.steps.length - 1 && (
                  <div
                    className="hidden lg:block absolute top-12 left-[calc(50%+3rem)] w-[calc(100%-4rem)] h-0.5 bg-teal-300 z-0"
                    aria-hidden="true"
                  ></div>
                )}
                <div className="text-center relative z-10">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-teal-500 to-teal-500 flex items-center justify-center shadow-xl shadow-teal-500/30">
                    <span className="text-3xl font-bold text-white">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">Perguntas Frequentes</h2>
            <p className="text-xl text-gray-600">Tire suas dúvidas sobre o Watizap</p>
          </div>
          <div className="space-y-4">
            {content.faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 text-gray-600">{faq.answer}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contato" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">Contato</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">{content.contact.title}</h2>
              <p className="text-gray-600 mb-8">{content.contact.subtitle}</p>
              
              <div className="space-y-4">
                {/* WhatsApp Button — controlado pelo toggle no CMS (visível por padrão) */}
                {content.contact.whatsapp_number && content.contact.whatsapp_enabled !== false && (
                  <a 
                    href={`https://wa.me/${content.contact.whatsapp_number.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 text-gray-600 hover:text-teal-700 transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-teal-100 group-hover:bg-teal-200 flex items-center justify-center transition-colors">
                      <svg className="w-6 h-6 text-teal-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">WhatsApp</div>
                      <div className="font-medium text-gray-900 group-hover:text-teal-700 transition-colors">
                        {content.contact.whatsapp_text || 'Falar pelo WhatsApp'}
                      </div>
                    </div>
                  </a>
                )}
                
                {/* Email */}
                <a 
                  href={`mailto:${content.contact.email_label}`}
                  className="flex items-center gap-4 text-gray-600 hover:text-teal-700 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-xl bg-teal-100 group-hover:bg-teal-200 flex items-center justify-center transition-colors">
                    <Mail className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">E-mail</div>
                    <div className="font-medium text-gray-900 group-hover:text-teal-700 transition-colors">{content.contact.email_label}</div>
                  </div>
                </a>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <form className="space-y-4" onSubmit={handleContactSubmit}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Seu e-mail</label>
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="bg-white border-gray-300"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sua mensagem</label>
                  <Textarea
                    placeholder="Como podemos ajudar?"
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    className="bg-white border-gray-300"
                    required
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={sendingContact}
                  className="w-full bg-gradient-to-r from-teal-600 to-teal-600 hover:from-teal-700 hover:to-teal-700 text-white disabled:opacity-50"
                >
                  {sendingContact ? 'Enviando...' : (
                    <>Enviar Mensagem <Send className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-slate-900 to-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{content.cta.title}</h2>
          <p className="text-xl text-gray-400 mb-10">{content.cta.subtitle}</p>
          <Button 
            onClick={() => document.getElementById('planos').scrollIntoView({ behavior: 'smooth' })}
            className="btn-shine-wrapper bg-gradient-to-r from-teal-500 to-teal-500 hover:from-teal-600 hover:to-teal-600 text-white text-lg px-7 py-4 h-auto shadow-xl shadow-teal-500/30"
            data-testid="cta-final-btn"
          >
            Assinar o Watizap
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-600 to-teal-600 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">Watizap</span>
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
              <a href="#funcionalidades" className="text-gray-400 hover:text-white transition-colors">Funcionalidades</a>
              <a href="#como-funciona" className="text-gray-400 hover:text-white transition-colors">Como Funciona</a>
              <a href="#faq" className="text-gray-400 hover:text-white transition-colors">FAQ</a>
              <a href="#contato" className="text-gray-400 hover:text-white transition-colors">Contato</a>
              <a
                href="/politica-de-privacidade"
                className="text-gray-400 hover:text-white transition-colors"
                data-testid="footer-privacy-link"
              >
                Privacidade
              </a>
              <a
                href="/termos-de-uso"
                className="text-gray-400 hover:text-white transition-colors"
                data-testid="footer-terms-link"
              >
                Termos
              </a>
              <button
                type="button"
                onClick={() => {
                  try { localStorage.removeItem('cookie_consent'); } catch (_) { /* noop */ }
                  window.location.reload();
                }}
                className="text-gray-400 hover:text-white transition-colors"
                data-testid="footer-cookies-link"
              >
                Cookies
              </button>
            </nav>
            <p className="text-gray-500 text-sm">© {copyrightYears()} Watizap • Todos os direitos reservados</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
